<template>
    <div>
        <div class="d-flex mb-3">
            <v-btn
                small
                class="ml-auto"
                color="primary"
                @click="showTips = !showTips">
                VIEW AVAILABLE FUNCTIONS
            </v-btn>
        </div>
        
        <v-dialog v-model="showTips" max-width="1400px" scrollable>
            <v-card>
                <v-card-title class="primary white--text py-3">
                    <v-icon left class="white--text">mdi-function-variant</v-icon>
                    <span class="text-h6">Available Functions Reference</span>
                    <v-spacer></v-spacer>
                    <v-text-field
                        v-model="searchQuery"
                        append-icon="mdi-magnify"
                        label="Search functions..."
                        single-line
                        hide-details
                        dark
                        dense
                        outlined
                        class="mt-0 pt-0 mr-2"
                        style="max-width: 300px;"
                    ></v-text-field>
                    <v-btn icon dark @click="showTips = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-0" style="max-height: 80vh;">
                    <!-- Header Row -->
                    <v-card flat class="list-header px-6 py-3" color="grey lighten-4">
                        <v-row no-gutters>
                            <v-col cols="4">
                                <span class="font-weight-bold text-body-1">Function</span>
                            </v-col>
                            <v-col cols="8">
                                <span class="font-weight-bold text-body-1">Description</span>
                            </v-col>
                        </v-row>
                    </v-card>
                    
                    <div class="px-4 py-2">
                        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>
                        <v-expansion-panels
                            v-model="openedPanel"
                            accordion
                            flat>
                        <v-expansion-panel
                            v-for="(fn, index) in filteredFunctions"
                            :key="'fn-' + index">
                            <v-expansion-panel-header class="function-header py-4 px-6">
                                <v-row no-gutters align="center">
                                    <v-col cols="4">
                                        <div class="d-flex align-center">
                                            <v-icon color="primary" small class="mr-2">mdi-function</v-icon>
                                            <span class="function-name">{{ fn.name }}</span>
                                        </div>
                                    </v-col>
                                    <v-col cols="8">
                                        <span class="function-description">
                                            {{ fn.shortDescription }}
                                        </span>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-header>
                            
                            <v-expansion-panel-content>
                                <v-card flat class="mt-2">
                                    <v-card-text class="px-6">
                                        <!-- Description -->
                                        <div class="mb-5">
                                            <div class="d-flex align-center mb-2">
                                                <v-icon small color="primary" class="mr-2">mdi-information-outline</v-icon>
                                                <h4 class="text-subtitle-1 font-weight-bold mb-0">Description</h4>
                                            </div>
                                            <p class="ml-7 mb-0 text-body-2">{{ fn.description }}</p>
                                        </div>

                                        <!-- Native PHP Functions List -->
                                        <div class="mb-5" v-if="fn.native && fn.functions">
                                            <div class="d-flex align-center mb-2">
                                                <v-icon small color="primary" class="mr-2">mdi-language-php</v-icon>
                                                <h4 class="text-subtitle-1 font-weight-bold mb-0">Available Functions</h4>
                                            </div>
                                            <v-card outlined class="ml-7">
                                                <v-card-text class="pa-3">
                                                    <v-row dense>
                                                        <v-col 
                                                            v-for="(phpFn, idx) in fn.functions" 
                                                            :key="'php-' + idx"
                                                            cols="12" sm="6" md="4">
                                                            <a 
                                                                :href="phpFn.url" 
                                                                target="_blank" 
                                                                class="d-flex align-center text-decoration-none mb-2 native-function-link">
                                                                <v-icon x-small color="primary" class="mr-1">mdi-open-in-new</v-icon>
                                                                <code class="native-function-name">{{ phpFn.name }}</code>
                                                                <span class="ml-2 text-caption text--secondary">{{ phpFn.description }}</span>
                                                            </a>
                                                        </v-col>
                                                    </v-row>
                                                </v-card-text>
                                            </v-card>
                                        </div>

                                        <!-- Usage Syntax -->
                                        <div class="mb-5">
                                            <div class="d-flex align-center mb-2">
                                                <v-icon small color="primary" class="mr-2">mdi-code-tags</v-icon>
                                                <h4 class="text-subtitle-1 font-weight-bold mb-0">Usage Syntax</h4>
                                            </div>
                                            <v-card outlined class="ml-7 syntax-card">
                                                <v-card-text class="pa-3">
                                                    <code class="syntax-code">{{ fn.usage }}</code>
                                                </v-card-text>
                                            </v-card>
                                        </div>

                                        <!-- Parameters -->
                                        <div class="mb-5" v-if="fn.parameters && fn.parameters.length > 0">
                                            <div class="d-flex align-center mb-2">
                                                <v-icon small color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
                                                <h4 class="text-subtitle-1 font-weight-bold mb-0">Parameters</h4>
                                            </div>
                                            <v-card outlined class="ml-7">
                                                <v-simple-table dense>
                                                    <template v-slot:default>
                                                        <thead>
                                                            <tr>
                                                                <th class="text-caption font-weight-bold">Parameter</th>
                                                                <th class="text-caption font-weight-bold">Type</th>
                                                                <th class="text-caption font-weight-bold">Required</th>
                                                                <th class="text-caption font-weight-bold">Description</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr v-for="(param, pIndex) in fn.parameters" :key="'param-' + pIndex">
                                                                <td><code class="param-name">{{ param.name }}</code></td>
                                                                <td>
                                                                    <v-chip x-small :color="getTypeColor(param.type)" text-color="white">
                                                                        {{ param.type }}
                                                                    </v-chip>
                                                                </td>
                                                                <td>
                                                                    <v-icon :color="param.required ? 'error' : 'grey'" small>
                                                                        {{ param.required ? 'mdi-check-circle' : 'mdi-minus-circle' }}
                                                                    </v-icon>
                                                                </td>
                                                                <td class="text-caption">{{ param.description }}</td>
                                                            </tr>
                                                        </tbody>
                                                    </template>
                                                </v-simple-table>
                                            </v-card>
                                        </div>

                                        <!-- Example -->
                                        <div class="mb-4" v-if="fn.example">
                                            <div class="d-flex align-center mb-2">
                                                <v-icon small color="success" class="mr-2">mdi-code-braces</v-icon>
                                                <h4 class="text-subtitle-1 font-weight-bold mb-0">Example</h4>
                                            </div>
                                            <v-card outlined class="ml-7 example-card">
                                                <v-card-text class="pa-3">
                                                    <div v-if="fn.example.input">
                                                        <div class="mb-2">
                                                            <span class="text-caption font-weight-bold">Function:</span>
                                                            <pre class="example-code mt-1">{{ fn.example.input }}</pre>
                                                        </div>
                                                        <v-icon small color="success" class="my-1">mdi-arrow-down-bold</v-icon>
                                                        <div class="mt-2">
                                                            <span class="text-caption font-weight-bold">Output:</span>
                                                            <pre class="example-code mt-1">{{ fn.example.output }}</pre>
                                                        </div>
                                                        <!-- Testing Note -->
                                                        <v-alert
                                                            v-if="fn.example.note"
                                                            dense
                                                            text
                                                            color="info"
                                                            class="mt-3 mb-0"
                                                            icon="mdi-information-outline">
                                                            <div class="text-caption">
                                                                <strong>Note:</strong> {{ fn.example.note }}
                                                            </div>
                                                        </v-alert>
                                                    </div>
                                                    <pre v-else class="example-code">{{ fn.example }}</pre>
                                                </v-card-text>
                                            </v-card>
                                        </div>

                                        <!-- Try It Out Section -->
                                        <div v-if="!fn.native" class="mt-5 pt-4" style="border-top: 2px dashed #e0e0e0;">
                                            <div class="d-flex align-center mb-3">
                                                <v-icon small color="orange darken-2" class="mr-2">mdi-test-tube</v-icon>
                                                <h4 class="text-subtitle-1 font-weight-bold mb-0">Try It Out</h4>
                                            </div>
                                            <v-card outlined class="ml-7">
                                                <v-card-text class="pa-4">
                                                    <div class="mb-3">
                                                        <v-text-field
                                                            v-model="testInputs[fn.name]"
                                                            label="Enter function with syntax"
                                                            :placeholder="fn.example.input"
                                                            outlined
                                                            dense
                                                            hint="Use the pipe (|) separator for parameters as shown in the example above"
                                                            persistent-hint
                                                        ></v-text-field>
                                                    </div>
                                                    
                                                    <div class="d-flex align-center mb-3">
                                                        <v-btn 
                                                            small 
                                                            color="primary"
                                                            :loading="testLoading[fn.name]"
                                                            @click="runTest(fn)">
                                                            <v-icon left small>mdi-play</v-icon>
                                                            Run Test
                                                        </v-btn>
                                                        <v-btn 
                                                            small 
                                                            text 
                                                            class="ml-2"
                                                            @click="clearTest(fn.name)">
                                                            Clear
                                                        </v-btn>
                                                    </div>

                                                    <!-- Test Result -->
                                                    <v-alert
                                                        v-if="testResults[fn.name]"
                                                        :type="testResults[fn.name].type"
                                                        dense
                                                        outlined
                                                        class="mt-3">
                                                        <div class="d-flex align-center">
                                                            <div class="flex-grow-1">
                                                                <div class="text-caption font-weight-bold mb-1">Result:</div>
                                                                <pre class="test-result-code mb-0">{{ testResults[fn.name].output }}</pre>
                                                            </div>
                                                        </div>
                                                    </v-alert>
                                                </v-card-text>
                                            </v-card>
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                    </div>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-4">
                    <v-chip small outlined color="grey">
                        <v-icon left x-small>mdi-information</v-icon>
                        {{ functions.length }} functions available
                    </v-chip>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="showTips = false">
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>


<script>
export default {
    data() {
        return {
            showTips: false,
            searchQuery: '',
            openedPanel: null,
            loading: false,
            functions: [],
            testInputs: {},
            testResults: {},
            testLoading: {}
        }
    },

    mounted() {
        this.fetchFunctions();
    },

    computed: {
        filteredFunctions() {
            if (!this.searchQuery) {
                return this.functions;
            }
            const query = this.searchQuery.toLowerCase();
            return this.functions.filter(fn => 
                fn.name.toLowerCase().includes(query) || 
                (fn.description && fn.description.toLowerCase().includes(query)) ||
                (fn.shortDescription && fn.shortDescription.toLowerCase().includes(query))
            );
        }
    },

    methods: {
        async fetchFunctions() {
            this.loading = true;
            try {
                const response = await this.$http.get('/api/fieldmappers/functions');
                console.log('Raw API response:', response.data);
                
                if (!response.data || !Array.isArray(response.data)) {
                    console.error('Invalid response format:', response.data);
                    this.functions = [];
                    return;
                }
                
                this.functions = this.parseFunctions(response.data);
                console.log('Parsed functions:', this.functions);
            } catch (error) {
                console.error('Error fetching functions:', error);
                console.error('Error details:', error.response);
                if (this.$toast) {
                    this.$toast.error('Failed to load functions');
                }
                this.functions = [];
            } finally {
                this.loading = false;
            }
        },

        parseFunctions(rawFunctions) {
            // Data is already in the correct format from config file
            // Initialize test inputs for each function
            rawFunctions.forEach(fn => {
                if (!this.testInputs[fn.name]) {
                    this.$set(this.testInputs, fn.name, '');
                    this.$set(this.testResults, fn.name, null);
                    this.$set(this.testLoading, fn.name, false);
                }
            });
            return rawFunctions;
        },

        async runTest(fn) {
            this.$set(this.testLoading, fn.name, true);
            this.$set(this.testResults, fn.name, null);

            try {
                const functionSyntax = this.testInputs[fn.name] || '';
                
                // Parse the function syntax (e.g., "concat|value1|value2| ")
                const parts = functionSyntax.split('|');
                const functionName = parts[0];
                const params = parts.slice(1);
                
                const response = await this.$http.post('/api/fieldmappers/test-function', {
                    function_name: functionName,
                    parameters: params
                });

                this.$set(this.testResults, fn.name, {
                    type: 'success',
                    output: response.data.result
                });
            } catch (error) {
                console.error('Test error:', error);
                this.$set(this.testResults, fn.name, {
                    type: 'error',
                    output: error.response?.data?.message || error.message || 'Test failed'
                });
            } finally {
                this.$set(this.testLoading, fn.name, false);
            }
        },

        clearTest(functionName) {
            this.$set(this.testInputs, functionName, '');
            this.$set(this.testResults, functionName, null);
        },

        getTypeColor(type) {
            const colors = {
                'string': 'blue darken-1',
                'number': 'green darken-1',
                'integer': 'green darken-1',
                'array': 'orange darken-1',
                'boolean': 'purple darken-1'
            };
            return colors[type] || 'grey';
        },

        openAndFocusFunction(functionName) {
            // Open the dialog
            this.showTips = true;
            
            // Find the index of the function
            const functionIndex = this.functions.findIndex(fn => fn.name === functionName);
            
            if (functionIndex !== -1) {
                // Use nextTick to ensure the dialog is rendered before opening the panel
                this.$nextTick(() => {
                    this.openedPanel = functionIndex;
                    
                    // Scroll to the function after a short delay
                    setTimeout(() => {
                        const panels = document.querySelectorAll('.v-expansion-panel');
                        if (panels[functionIndex]) {
                            panels[functionIndex].scrollIntoView({ 
                                behavior: 'smooth', 
                                block: 'center' 
                            });
                        }
                    }, 300);
                });
            }
        }
    }
}
</script>

<style scoped>
.list-header {
    border-bottom: 2px solid #1976d2;
    position: sticky;
    top: 0;
    z-index: 1;
}

.function-header {
    background-color: #ffffff;
    border-bottom: 1px solid #e0e0e0;
    transition: all 0.2s ease;
}

.function-header:hover {
    background-color: #f5f9ff;
}

.function-name {
    font-family: 'Courier New', Monaco, monospace;
    font-size: 15px;
    font-weight: 700;
    color: #1565c0;
    letter-spacing: 0.3px;
}

.function-description {
    font-size: 14px;
    color: #424242;
    line-height: 1.5;
}

.syntax-card {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
}

.syntax-code {
    font-family: 'Courier New', Monaco, monospace;
    font-size: 14px;
    color: #d63384;
    background-color: transparent;
    font-weight: 500;
    display: block;
    line-height: 1.5;
}

.param-name {
    font-family: 'Courier New', Monaco, monospace;
    font-size: 12px;
    color: #0d6efd;
    background-color: #e7f1ff;
    padding: 2px 6px;
    border-radius: 3px;
    font-weight: 600;
}

.example-card {
    border: 2px solid #e0e0e0;
    transition: all 0.2s ease;
}

.example-card:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.example-code {
    font-family: 'Courier New', Monaco, monospace;
    font-size: 12px;
    line-height: 1.6;
    overflow-x: auto;
    white-space: pre-wrap;
    margin: 0;
    color: #212529;
}

.test-result-code {
    font-family: 'Courier New', Monaco, monospace;
    font-size: 12px;
    line-height: 1.6;
    overflow-x: auto;
    white-space: pre-wrap;
    margin: 0;
    color: #212529;
}

.output-code {
    font-weight: 600;
    color: #198754;
}

.native-function-link {
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
}

.native-function-link:hover {
    background-color: #f5f5f5;
}

.native-function-name {
    font-family: 'Courier New', Monaco, monospace;
    font-size: 13px;
    color: #1565c0;
    font-weight: 600;
}

pre {
    margin: 0;
}

h4 {
    color: #1976d2;
}

/* Custom scrollbar for dialog */
::-webkit-scrollbar {
    width: 8px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
    background: #555;
}
</style>